// Pure formatting helpers for display values. No React, no DOM. All functions
// are total — never throw, always return a string the UI can render.
//
// See UI-SPEC § 8 (Copywriting Contract) — the strings these helpers emit
// must match § 8 byte-exactly.

/**
 * Format a 0–100 integer score for display. `null` → em-dash `—`.
 *
 * @example formatScore(82)   // '82'
 * @example formatScore(null) // '—'
 */
export function formatScore(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return String(Math.round(n));
}

/**
 * Format a radius in miles. Always 1 decimal, always trailing ` mi`.
 *
 * @example formatRadius(0.5) // '0.5 mi'
 * @example formatRadius(1)   // '1.0 mi'
 */
export function formatRadius(mi: number): string {
  return `${mi.toFixed(1)} mi`;
}

export type RecencyTier = 'new' | 'recent' | 'older' | 'stale';

/**
 * Compute days-since-listing + recency tier per UI-SPEC § 3.10 / D2.
 * Future-dated `createdAt` is clamped to 0 days.
 *
 * Tiers:
 * - `new`    ≤ 7 days
 * - `recent` 8 – 30 days
 * - `older`  31 – 90 days
 * - `stale`  > 90 days
 */
export function formatRecencyDays(
  createdAt: string,
  now = Date.now(),
): { days: number; tier: RecencyTier } {
  const ts = Date.parse(createdAt);
  if (Number.isNaN(ts)) return { days: 0, tier: 'new' };
  const diffMs = now - ts;
  const days = Math.max(0, Math.floor(diffMs / 86_400_000));
  let tier: RecencyTier;
  if (days <= 7) tier = 'new';
  else if (days <= 30) tier = 'recent';
  else if (days <= 90) tier = 'older';
  else tier = 'stale';
  return { days, tier };
}

/**
 * Truncate an address to `max` characters with an ellipsis. Used for
 * the "Top Fit" + "Freshest" stat tiles where horizontal space is tight.
 *
 * @example formatAddress('123 Coral Way, Tampa, FL 33606', 24)
 *          // '123 Coral Way, Tampa, F…'
 */
export function formatAddress(full: string, max = 24): string {
  if (full.length <= max) return full;
  return `${full.slice(0, Math.max(0, max - 1))}…`;
}

/**
 * Format a price in USD. Input is whole dollars (NOT cents) per the
 * `listing_details.price` schema (INTEGER). `null` → em-dash.
 * Prices ≥ 1000 abbreviated as `$Nk`, ≥ 1_000_000 as `$N.NNm`.
 *
 * @example formatPrice(485000)  // '$485k'
 * @example formatPrice(1250000) // '$1.25m'
 * @example formatPrice(null)    // '—'
 */
export function formatPrice(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return '—';
  if (price >= 1_000_000) {
    const m = price / 1_000_000;
    return `$${m.toFixed(2).replace(/\.?0+$/, '')}m`;
  }
  if (price >= 1_000) {
    return `$${Math.round(price / 1_000)}k`;
  }
  return `$${price}`;
}

// ============================================================
// Phase 07 — Jobs UI helpers
// ============================================================

/**
 * Parse SQLite-flavored datetime strings as UTC. SQLite's `datetime()` writes
 * `YYYY-MM-DD HH:MM:SS` without a timezone offset; the v1.0 codebase has
 * always treated those as UTC, and Plan 07-01 preserves that convention.
 * ISO strings with `T` and offset/Z are passed through untouched.
 *
 * @internal — exported for the `format.test.ts` suite if/when added.
 */
function parseUtcLike(iso: string): number {
  return Date.parse(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
}

/**
 * Format an ISO/SQLite datetime string as a relative time string.
 * Returns: "just now", "Ns ago", "Nm ago", "Nh ago", "Nd ago", or "—" for
 * null / unparseable input. Future-dated input clamps to "just now".
 *
 * @example formatRelativeTime('2026-04-26 09:00:00')   // depends on now
 * @example formatRelativeTime(null)                    // '—'
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = parseUtcLike(iso);
  if (!Number.isFinite(ms)) return '—';
  const deltaSec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (deltaSec < 5) return 'just now';
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.round(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

/**
 * Format a duration in seconds for the jobs UI meta lines.
 * - `< 60s`   → `"0:23s"`
 * - `< 1h`    → `"1m 32s"`
 * - `>= 1h`   → `"1h 4m"`
 * - `null` / negative / non-finite → `"—"`
 *
 * @example formatDuration(47)    // '0:47s'
 * @example formatDuration(133)   // '2m 13s'
 * @example formatDuration(3840)  // '1h 4m'
 * @example formatDuration(null)  // '—'
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 0) return '—';
  const whole = Math.round(seconds);
  if (whole < 60) return `0:${String(whole).padStart(2, '0')}s`;
  const min = Math.floor(whole / 60);
  const sec = whole % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}h ${remMin}m`;
}

/**
 * Convert AgentName (snake_case) or hyphenated server form to a display label.
 * `dog_friendliness` or `dog-friendliness` → `Dog Friendliness`.
 * `compiled` or `report-compiler` → `Compiled` (UI-SPEC § 8 copywriting).
 *
 * Total — never throws; unknown strings get a best-effort title-case.
 */
export function formatAgentLabel(name: string): string {
  const SPECIAL: Record<string, string> = {
    compiled: 'Compiled',
    'report-compiler': 'Compiled',
  };
  if (SPECIAL[name]) return SPECIAL[name];
  return name
    .replace(/-/g, '_')
    .split('_')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Compute the elapsed seconds between two timestamps. If `endIso` is null
 * (the agent is still running), uses `nowMs` (defaults to Date.now()) so the
 * caller can provide a controlled clock for tests / live tickers.
 *
 * Returns null if `startIso` is null or unparseable. Negative durations clamp
 * to 0 (clock-skew defense).
 */
export function elapsedSeconds(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!startIso) return null;
  const startMs = parseUtcLike(startIso);
  if (!Number.isFinite(startMs)) return null;
  if (endIso) {
    const endMs = parseUtcLike(endIso);
    if (Number.isFinite(endMs)) return Math.max(0, Math.round((endMs - startMs) / 1000));
  }
  return Math.max(0, Math.round((nowMs - startMs) / 1000));
}
